// Diagnose in five. Clue cards are dealt as the server releases them (question.clues grows; a clue is never shown
// before its snapshot arrives), in reading order above the answers (Haseeb, 26 Oct 2026). While the question is open the
// answers are docked at the foot of the screen and the cards scroll in the room above them (dock.ts), so a card landing
// never moves an answer under a thumb; a new card is brought into view. At the reveal the dock lets go. The points meter
// follows the server's clock and its scoring rule exactly, with one rung per clue that can be dealt before the
// deadline (question.cluesTotal says how many there are). When a clue's time has come but its snapshot is still on
// the way, a card says so, so the meter never steps down without a reason on screen.
import { Fragment, memo, useEffect, useId, useMemo, useRef, useState, type RefObject } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Stem } from '@/components/Stem';
import { sfx } from '@/audio/sfx';
import { serverNow } from '@/lib/api';
import type { FormatViewProps, LiveQuestion, AnswerPhase } from './types';
import { effectivePhase } from './layout';
import { clueClock, clueMax, ladder, shownForElapsed, type ClueClock } from './clueMeter';
import { recallLock, rememberLock, type LockInfo } from './lockMemo';
import { useServerStamp } from './serverStamp';
import { CrossGlyph, OptionList, TickGlyph, useAnswerTap } from './parts';
import { useServerNow } from './useServerNow';
import { useDockRoom } from './dock';
import { Split, useWide } from './split';

type Verdict = 'correct' | 'wrong';
const NO_CLUES: string[] = [];

export function CluesView(props: FormatViewProps) {
  const { question: q, myOptionId, reveal } = props;
  const phase = effectivePhase(props.phase, myOptionId, reveal);
  const clues = q.clues ?? NO_CLUES;
  const now = useServerNow(phase === 'revealed' ? null : 250);
  const clock = clueClock(q, now);
  const { tap, live } = useAnswerTap({ ...props, phase }, () => {
    const t = serverNow();
    rememberLock(q, { at: t, shown: clueClock(q, t).shown, dealt: clues.length });
  });
  const tapNote = myOptionId ? recallLock(q) : undefined;
  const stamp = useServerStamp(q, myOptionId);
  // The server's stamp, once it has accepted the answer, says how many clues it counted; the tap-time note says
  // how many cards this phone was showing.
  const lock = useMemo((): LockInfo | undefined => {
    if (stamp === undefined) return tapNote;
    const shown = shownForElapsed(q, stamp);
    return { at: q.startsAt + stamp, shown, dealt: tapNote?.dealt ?? shown };
  }, [q, stamp, tapNote]);
  const verdict: Verdict | undefined = phase === 'revealed' && reveal && myOptionId ? (myOptionId === reveal.correctId ? 'correct' : 'wrong') : undefined;

  // A card is dealt: the sound twin of the deal animation (never for cards already on the table at mount).
  const seen = useRef(clues.length);
  useEffect(() => {
    if (clues.length > seen.current && phase !== 'revealed') sfx.play('deal');
    seen.current = clues.length;
  }, [clues.length, phase]);

  // Docked until the reveal, on the question screen only (the reveal screen shows a voided question as locked, with no
  // answering): the answers stay put at the foot of the screen while cards are dealt above them.
  const wide = useWide();
  const docked = phase !== 'revealed' && Boolean(props.onAnswer) && !wide;
  const dock = useDockRoom(docked);
  // Docked answers are short rows: a tile with the mark beside the words would break long names mid-word on a small phone.
  const options = <OptionList options={q.options} phase={phase} myOptionId={myOptionId} reveal={reveal} live={live} onTap={tap} dense={docked} layout={docked ? 'rows' : undefined} />;

  return (
    <div ref={dock.wrap} className="fx-format" data-format="clues" data-phase={phase} data-display="phones" data-dock={docked ? '' : undefined} data-layout={wide ? 'split' : undefined}>
      <Split wide={wide}
        left={<>
          {q.stem && <Stem text={q.stem} />}
          <ClueMeter q={q} clock={clock} now={now} phase={phase} lock={lock} verdict={verdict} answered={Boolean(myOptionId)} />
          <div ref={dock.room} className="fx-dock-room" data-part="clue-room">
            <ClueCards clues={clues} due={clock.due} phase={phase} lock={lock} verdict={verdict} room={docked ? dock.room : undefined} dock={docked ? dock.dock : undefined} onPage={wide} />
          </div>
        </>}
        right={docked
          ? <div ref={dock.dock as RefObject<HTMLDivElement | null>} className="fx-dock" data-docked="" data-part="answer-dock">{options}</div>
          : options} />
    </div>
  );
}

function ClueMeter({ q, clock, now, phase, lock, verdict }: {
  q: LiveQuestion; clock: ClueClock; now: number; phase: AnswerPhase; lock?: LockInfo; verdict?: Verdict; answered: boolean;
}) {
  const id = useId();
  const reduce = useReducedMotion() ?? false;
  const rungs = ladder(clock.n, clueMax(q), q.multiplier ?? 1, clock.reach);
  const clampK = (k: number) => Math.min(rungs.length, Math.max(1, k));
  // Answers close at the deadline, so from then on nothing is on offer while the answer is worked out.
  const over = phase === 'answering' && now >= q.deadlineAt;
  // The rung that matters: the live one while answering, else where this player locked in (unknown: none).
  const k = over ? undefined : phase === 'answering' ? clampK(clock.shown) : lock ? clampK(lock.shown) : undefined;
  const next = phase === 'answering' && !over ? clock.nextAt : undefined;
  const secs = next !== undefined ? Math.max(0, Math.ceil((next - now) / 1000)) : 0;
  const lockTone = lock ? (verdict ?? 'locked') : undefined;
  // Never "Locked in" (the footer's chip says it, and the clue cards mark the spot) and never "No answer" (the reveal's
  // headline says it). The caption names the highlighted rung: what it is worth now, what the answer is worth if right,
  // or, at the reveal, the scale of points.
  const caption = over ? "Time's up" : phase === 'answering' ? 'Worth now' : phase === 'locked' && lock ? 'Worth if right' : 'Points by clue';
  // The meter just stepped down: float the points lost off the top (keyed by the step, so it plays once).
  const prevK = useRef(k);
  const [drop, setDrop] = useState<{ k: number; amount: number }>();
  useEffect(() => {
    const before = prevK.current;
    prevK.current = k;
    if (phase === 'answering' && k !== undefined && before !== undefined && k > before) setDrop({ k, amount: rungs[before - 1] - rungs[k - 1] });
    // rungs follow n, maxPoints and the multiplier, which do not change within a question
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, phase]);
  const remainingFrac = next !== undefined ? Math.min(1, Math.max(0, (next - now) / clock.interval)) : 0;

  return (
    <section className="fx-ladder" aria-label="Points on offer">
      <div className="fx-ladder-top">
        <div className="fx-ladder-now">
          <span className="fx-ladder-caption">{caption}</span>
          {/* The highlighted rung shows the points, so the figure is said once on screen and read here for screen readers
              (the rungs are hidden from them). At the reveal the headline's points say what it earned. */}
          {k !== undefined && phase !== 'revealed' && <span className="fx-ladder-points sr-only" aria-live="off">{rungs[k - 1].toLocaleString('en-GB')}<small>points</small></span>}
        </div>
        {phase === 'answering' && !over && (
          <span className="fx-ladder-next" data-soon={next !== undefined && secs <= 2 ? '' : undefined}>
            {next !== undefined && k !== undefined && k < rungs.length
              ? <>Next clue in <b>{secs} s</b></>
              : <>Every clue is out</>}
          </span>
        )}
        {drop && !reduce && (
          <motion.span key={`drop-${drop.k}`} className="fx-ladder-drop" aria-hidden="true"
            initial={{ opacity: 1, y: 0 }} animate={{ opacity: 0, y: -24 }} transition={{ duration: 0.95, ease: 'easeOut' }}>
            −{drop.amount.toLocaleString('en-GB')}
          </motion.span>
        )}
      </div>
      <div className="fx-rungs" aria-hidden="true">
        {rungs.map((pts, i) => {
          const rung = i + 1;
          const at = k === undefined ? 'next' : rung < k ? 'past' : rung === k ? 'now' : 'next';
          return (
            <span key={rung} className="fx-rung" data-at={at} data-lock={lock && k === rung ? lockTone : undefined}>
              {at === 'now' && <motion.i layoutId={`rung-${id}`} className="fx-rung-now" transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 32 }} />}
              <span>{pts.toLocaleString('en-GB')}</span>
              <small>{rung === 1 ? '1 clue' : `${rung} clues`}</small>
            </span>
          );
        })}
      </div>
      {/* The bar's slot stays while answering, empty once every clue is out, so the last clue moves nothing below it. */}
      {phase === 'answering' && (
        <div className="fx-ladder-bar" aria-hidden="true" data-empty={next === undefined ? '' : undefined}>
          {next !== undefined && <motion.i key={`bar-${k}`} initial={{ scaleX: remainingFrac }} animate={{ scaleX: 0 }} transition={{ duration: Math.max(0.05, (next - now) / 1000), ease: 'linear' }} />}
        </div>
      )}
    </section>
  );
}

/** Memoised: the clock ticks four times a second, the cards change only when a clue falls due or lands. */
const ClueCards = memo(function ClueCards({ clues, due, phase, lock, verdict, room, dock, onPage }: {
  clues: string[]; due: number; phase: AnswerPhase; lock?: LockInfo; verdict?: Verdict;
  /** The room the cards scroll in and the docked answers under it, while docked. */
  room?: RefObject<HTMLDivElement | null>; dock?: RefObject<HTMLElement | null>;
  /** On a desktop (no dock): a new card is brought into view on the page. */
  onPage?: boolean;
}) {
  const reduce = useReducedMotion() ?? false;
  const deal = phase !== 'revealed';
  const newest = clues.length - 1;
  // A clue whose time has come is on its way until its snapshot lands; nothing is said about clues not yet due.
  const waiting = deal && (clues.length === 0 || due > clues.length);
  // The line sits after the last card that was showing when the player locked in, or before the first card when the
  // answer came before any clue.
  const lineAfter = lock && phase !== 'answering' ? (lock.dealt === 0 ? -1 : newest >= 0 ? Math.min(newest, lock.dealt - 1) : undefined) : undefined;
  // Where, not whether: the footer's chip already says the answer is locked in.
  const lineText = lock && lock.dealt === 0 ? 'You answered before the first clue' : 'You answered here';
  const lockLine = (key: string) => (
    <motion.li key={key} layout={!reduce} className="fx-lockline" data-tone={verdict}
      initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }}>
      {verdict === 'correct' ? <TickGlyph size={16} stroke={3} /> : verdict === 'wrong' ? <CrossGlyph size={16} stroke={3} /> : null}
      {lineText}
    </motion.li>
  );

  // The cards stay in view above the docked answers: on first show the page is moved just enough that the room clears
  // the dock (a short phone), and a new card scrolls the room to its foot, where the newest card sits.
  const shown = useRef(clues.length);
  useEffect(() => {
    if (deal) keepInView(room?.current, dock?.current, false, false);
    // on first show only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    if (clues.length > shown.current && deal) {
      keepInView(room?.current, dock?.current, true, !reduce);
      if (onPage) [...(listRef.current?.querySelectorAll<HTMLElement>('.fx-clue') ?? [])].pop()?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }
    shown.current = clues.length;
  }, [clues.length, deal, reduce, room, dock, onPage]);
  // "Clue n on its way" sits under the newest card: when it appears, the room scrolls to show it, so the meter never
  // steps down without a reason on screen.
  const waitShown = useRef(waiting);
  useEffect(() => {
    if (waiting && !waitShown.current) keepInView(room?.current, dock?.current, true, !reduce);
    waitShown.current = waiting;
  }, [waiting, reduce, room, dock]);

  return (
    <ol ref={listRef} className="fx-clues" aria-label="Clues" aria-live="polite">
      {lineAfter !== undefined && lineAfter < 0 && lockLine('lock-first')}
      {clues.map((text, i) => (
        <Fragment key={i}>
          <motion.li
            key={`clue-${i}`}
            layout={!reduce}
            className="fx-clue"
            data-newest={i === newest && deal ? '' : undefined}
            initial={deal && !reduce ? { opacity: 0, x: 56, y: -16, rotate: 7, scale: 0.9 } : false}
            animate={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 360, damping: 26 }}
          >
            <span className="fx-clue-n" aria-hidden="true">{i + 1}</span>
            <span className="fx-clue-body">
              {/* The badge shows the number; the words are for screen readers. */}
              <span className="sr-only">Clue {i + 1}</span>
              <span className="fx-clue-text">{text}</span>
            </span>
          </motion.li>
          {lineAfter === i && lockLine(`lock-${i}`)}
        </Fragment>
      ))}
      {waiting && (
        <motion.li key={`wait-${clues.length}`} className="fx-clue-wait" data-calm={reduce ? '' : undefined}
          initial={reduce ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {clues.length === 0 ? 'First clue on its way' : `Clue ${clues.length + 1} on its way`}
        </motion.li>
      )}
    </ol>
  );
});

/**
 * Keeps the clue room in view above the docked answers: optionally scrolls the room to its foot (the newest card), then
 * moves the page by the amount the room's foot sits under the dock, if any. Nothing happens without both elements.
 */
export function keepInView(room: HTMLElement | null | undefined, dock: HTMLElement | null | undefined, toNewest: boolean, smooth: boolean): void {
  if (!room || !dock) return;
  const behavior: ScrollBehavior = smooth ? 'smooth' : 'auto';
  if (toNewest) {
    if (typeof room.scrollTo === 'function') room.scrollTo({ top: room.scrollHeight, behavior });
    else room.scrollTop = room.scrollHeight;
  }
  const under = room.getBoundingClientRect().bottom - (window.innerHeight - dock.getBoundingClientRect().height);
  if (under > 1 && typeof window.scrollBy === 'function') window.scrollBy({ top: Math.ceil(under), behavior });
}
